import { useState, useRef, useEffect } from 'react';
import { trpc } from '../api/trpc';
import { useToast } from './Toast';

interface Props {
  conversationId: string;
  onClose: () => void;
}

export function ParticipantPanel({ conversationId, onClose }: Props) {
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();
  const utils = trpc.useUtils();

  const participants = trpc.conversations.getParticipants.useQuery({ conversationId });
  const searchUsers = trpc.conversations.searchUsers.useQuery({ query: searchQuery });
  const addParticipant = trpc.conversations.addParticipant.useMutation();
  const removeParticipant = trpc.conversations.removeParticipant.useMutation();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Close on click outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const panel = document.querySelector('.participant-panel');
      if (panel && !panel.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  const participantIds = new Set(
    (participants.data ?? []).map((p: { userId: string }) => p.userId),
  );

  // Filter search results to exclude existing participants
  const searchResults = (searchUsers.data ?? []).filter(
    (u: { id: string }) => !participantIds.has(u.id),
  );

  async function handleAdd(userId: string, name: string) {
    try {
      await addParticipant.mutateAsync({ conversationId, userId });
      showToast(`${name} added to chat`, 'success');
      setSearchQuery('');
      await participants.refetch();
      await utils.conversations.get.invalidate({ conversationId });
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to add participant', 'error');
    }
  }

  async function handleRemove(userId: string, name: string) {
    try {
      await removeParticipant.mutateAsync({ conversationId, userId });
      showToast(`${name} removed from chat`, 'info');
      await participants.refetch();
      await utils.conversations.get.invalidate({ conversationId });
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to remove participant', 'error');
    }
  }

  return (
    <div className="participant-panel">
      <div className="participant-panel-header">
        <span className="participant-panel-title">People</span>
        <button className="participant-panel-close" onClick={onClose}>&times;</button>
      </div>

      {/* Search to add */}
      <div className="participant-search-wrap">
        <input
          ref={inputRef}
          className="participant-search-input"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Add people by name or email…"
        />
      </div>

      {/* Available users to add */}
      {searchResults.length > 0 && (
        <div className="participant-search-results">
          <div className="participant-list-header">
            {searchQuery ? 'Results' : 'Available to add'}
          </div>
          {searchResults.map((user: { id: string; name: string; email: string }) => (
            <button
              key={user.id}
              className="participant-search-result"
              onClick={() => { void handleAdd(user.id, user.name); }}
              disabled={addParticipant.isPending}
            >
              <div className="participant-avatar-sm">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div className="participant-search-info">
                <span className="participant-search-name">{user.name}</span>
                <span className="participant-search-email">{user.email}</span>
              </div>
              <span className="participant-add-icon">+</span>
            </button>
          ))}
        </div>
      )}

      {searchResults.length === 0 && !searchUsers.isLoading && (
        <div className="participant-search-empty">
          {searchQuery ? 'No users found' : 'All tenant users are already in this chat'}
        </div>
      )}

      {/* Divider */}
      <div className="participant-divider" />

      {/* Current participants */}
      <div className="participant-list-header">
        In this chat ({participants.data?.length ?? 0})
      </div>
      <div className="participant-list">
        {(participants.data ?? []).map((p: { userId: string; name: string; email: string; role: string }) => (
          <div key={p.userId} className="participant-row">
            <div className="participant-avatar-sm">
              {p.name.charAt(0).toUpperCase()}
            </div>
            <div className="participant-info">
              <span className="participant-name">{p.name}</span>
              <span className="participant-email">{p.email}</span>
            </div>
            {p.role === 'owner' ? (
              <span className="participant-role-badge">Owner</span>
            ) : (
              <button
                className="participant-remove-btn"
                title={`Remove ${p.name}`}
                onClick={() => { void handleRemove(p.userId, p.name); }}
                disabled={removeParticipant.isPending}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <line x1="4" y1="8" x2="12" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
